import {Inject, Injectable, LOCALE_ID, OnInit} from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpParams,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import {BehaviorSubject, EMPTY, Observable, throwError, timer} from 'rxjs';
import {catchError, filter, map, switchMap, take} from 'rxjs/operators';
import {GfxMiscSrv} from './gfx.misc.service';
import {GfxStoreService} from './gfx.store.service';
import {AuthService} from './auth.service';
import {NgxSpinnerService} from 'ngx-spinner';
import {APP_BROWSER_SESSION_KEYNAME, FINGERPRINT_KEY, MSG_SUCCESS, MSG_SUCCESS_DEL,} from '../enums/constants';
import {CookieService} from 'ngx-cookie-service';
import {environment} from '../../environments/environment';
import {TokenRefreshService} from './token-refresh.service';
import {SKIP_SPINNER} from './GfxHttpSrv';

@Injectable({
    providedIn: 'root',
})
export class GfxHttpInterceptor implements HttpInterceptor, OnInit {
    isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
        null
    );

    constructor(
        private cookieService: CookieService,
        private miscSrv: GfxMiscSrv,
        private spinner: NgxSpinnerService,
        private authService: AuthService,
        @Inject(LOCALE_ID) private locale: string,
        private tokenRefreshService: TokenRefreshService,
        private store: GfxStoreService
    ) {
    }

    ngOnInit(): void {
    }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return new Observable<HttpEvent<any>>((observer) => {
            const tokenXSRF = this.cookieService.get('XSRF-TOKEN');
            this.handleInterception(request, next, tokenXSRF)
                .then((result) => {
                    result.subscribe({
                        next: (event) => observer.next(event),
                        error: (err) => observer.error(err),
                        complete: () => observer.complete(),
                    });
                })
                .catch((err) => {
                    observer.error(err);
                });
        });
    }

    private async handleInterception(
        request: HttpRequest<any>,
        next: HttpHandler
        , tokenXSRF: string): Promise<Observable<HttpEvent<any>>> {
        const backendUrl = environment.BackendUrl;
        const capturedUrl = request.url;
        const headerSettingsLogin: { [name: string]: string | string[] } = {};
        headerSettingsLogin['Accept-Language'] = this.locale;
        headerSettingsLogin['X-XSRF-TOKEN'] = tokenXSRF;
        headerSettingsLogin['Ident'] = sessionStorage.getItem(FINGERPRINT_KEY);
        if (capturedUrl === 'auth/login') {
            const loginName = request.body.usr;
            headerSettingsLogin['browserKeySession'] = this.cookieService.get(
                APP_BROWSER_SESSION_KEYNAME + loginName.toUpperCase()
            );
        } else {
            if (this.store.curUser) {
                if (this.store.curUser.access_token) {
                    headerSettingsLogin['Authorization'] = this.store.curUser.access_token;
                }
                headerSettingsLogin['browserKeySession'] = this.cookieService.get(
                    APP_BROWSER_SESSION_KEYNAME + this.store.curUser.login.toUpperCase()
                );
            }
        }

        const newHeaderLogin = new HttpHeaders(headerSettingsLogin);

        if (
            capturedUrl.startsWith('http://') ||
            capturedUrl.startsWith('https://')
        ) {
            return next.handle(request);
        }

        // here we add gfx backend api url
        request = request.clone({
            url: `${backendUrl}/${capturedUrl}`,
            headers: newHeaderLogin, withCredentials: true
        });

        if (capturedUrl === 'auth/login') {
            if (this.store.onGoingPostOrPut) {
                return EMPTY;
            } else {
                this.store.onGoingPostOrPut = true;
            }
            return next.handle(request).pipe(
                map((event: HttpEvent<any>) => {
                    return event;
                }),
                catchError((error: HttpErrorResponse) => {
                    this.miscSrv.openDialogView({data: error});
                    return throwError(error);
                })
            );
        }

        if (request.url.includes('auth/refreshtoken') || request.url.includes('auth/twoFaOtpValidate')) {
            return next.handle(
                request.clone({
                    headers: newHeaderLogin,
                    withCredentials: true
                })
            );
        }

        if (this.store.curUser && this.store.curUser.access_token && this.authService.isTokenExpired(this.store.curUser.access_token)) {
            if (!this.tokenRefreshService.isRefreshingInProgress) {
                this.tokenRefreshService.startRefresh();

                return this.authService.postRefresh().pipe(
                    switchMap((res: any) => {
                        this.store.curUser.access_token = 'Bearer ' + res.access_token;
                        this.store.changeUser(this.store.curUser);
                        this.tokenRefreshService.completeRefresh(this.store.curUser.access_token);
                        return this.retryFailedRequests(request, next, tokenXSRF);
                    }),
                    catchError((err) => {
                        this.store.changeUser(null);
                        this.tokenRefreshService.failRefresh(err);
                        if (err.status == 401) {
                            this.authService.logout();
                        }
                        return throwError(err);
                    })
                );
            } else {
                return this.tokenRefreshService.getRefreshObservable().pipe(
                    filter(token => token !== null),
                    take(1),
                    switchMap(token => {
                        return this.retryFailedRequests(request, next, tokenXSRF);
                    }),
                    catchError(error => {
                        this.store.changeUser(null);
                        this.authService.logout();
                        return throwError(error);
                    })
                );
            }
        }

        let changedRequest = request;

        if (this.tokenRefreshService.isRefreshingInProgress) {
            return this.tokenRefreshService.getRefreshObservable().pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {

                    const newRequest = this.addTokenToRequest(request);
                    return next.handle(newRequest);
                }),
                catchError(error => {
                    return throwError(error);
                })
            );
        }
        const headerSettings: { [name: string]: string | string[] } = {};
        for (const key of request.headers.keys()) {
            headerSettings[key] = request.headers.getAll(key);
        }

        headerSettings['Ident'] = sessionStorage.getItem(FINGERPRINT_KEY);

        headerSettings['X-XSRF-TOKEN'] = tokenXSRF;
        if (this.store.curUser && this.store.curUser.access_token) {
            headerSettings['Authorization'] = this.store.curUser.access_token;
        }
        headerSettings['Accept-Language'] = this.locale;
        const newHeader = new HttpHeaders(headerSettings);

        // cleaning params
        let cleanedParams = new HttpParams();
        request.params.keys().forEach((x) => {
            if (request.params.get(x) != undefined) {
                cleanedParams = cleanedParams.append(x, request.params.get(x));
            }
        });

        changedRequest = request.clone({
            headers: newHeader,
            params: cleanedParams, withCredentials: true
        });

        if (
            changedRequest.method == 'POST' ||
            changedRequest.method == 'DELETE' ||
            changedRequest.method == 'PUT'
        ) {
            if (this.store.onGoingPostOrPut) {
                return EMPTY;
            } else {
                this.store.onGoingPostOrPut = true;
            }
        }
        const skipSpinner = request.context.get(SKIP_SPINNER);

        let t: any;

        if (!skipSpinner) {
            t = setTimeout(() => {
                this.spinner.show();
            }, 1000);
        }

        return next.handle(changedRequest).pipe(
            map((event: HttpEvent<any>) => {
                timer(700).subscribe(() => this.spinner.hide());
                if (event instanceof HttpResponse) {
                    clearTimeout(t);
                    if (
                        changedRequest &&
                        (changedRequest.method == 'POST' || changedRequest.method == 'PUT')
                    ) {
                        this.store.onGoingPostOrPut = false;
                        this.store.central_msg_success = MSG_SUCCESS;
                    } else if (changedRequest && changedRequest.method == 'DELETE') {
                        this.store.onGoingPostOrPut = false;
                        if (this.store.central_msg_success_del) {
                            this.miscSrv.openSnackBarDelByState();
                        }

                        this.store.central_msg_success_del = MSG_SUCCESS_DEL;
                    }
                }

                return event;
            }),
            catchError((error: HttpErrorResponse) => {
                console.log(error);
                this.store.onGoingPostOrPut = false;
                timer(700).subscribe(() => this.spinner.hide());
                clearTimeout(t);
                if (error.status === 429) {
                    this.miscSrv.openDialogView({
                        data: {
                            message:
                                'Trop de requêtes envoyées. Veuillez réessayer plus tard.',
                            status: 429,
                            error: true,
                        },
                    });
                    return throwError(() => new Error('Too Many Requests'));
                }

                this.miscSrv.openDialogView({data: error});
                return throwError(error);
            })
        );
    }

    private retryFailedRequests(
        req: HttpRequest<any>,
        next: HttpHandler
        , tokenXSRF: string): Observable<HttpEvent<any>> {
        return this.refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => {

                const reqClone = req.clone({
                    headers:
                        req.headers.set('X-XSRF-TOKEN', tokenXSRF)
                            .set('Ident', sessionStorage.getItem(FINGERPRINT_KEY))
                    , withCredentials: true
                });

                return next.handle(reqClone);
            })
        );
    }

    private addTokenToRequest(request: HttpRequest<any>): HttpRequest<any> {
        if (!this.store.curUser) {
            return request;
        }

        return request.clone({
            headers: request.headers.set('Authorization', this.store.curUser.access_token), withCredentials: true
        });
    }
}
